import * as React from "react";
import { v4 as uuidv4 } from "uuid";
import { formInitialize, formSet, formUninitialize, useCpFormSubmit } from "../gvc/testNewForm/utils";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { formActions } from "../../store/formSlice";
import { Modal, notification } from "antd";
import { linksOf, request } from "../../services/cpln";
import { clearLogoCacheForSlugs, fetchCustomLogo, selectLightSlug, selectDarkSlug } from "../../store/restSlice";
import { updateOrg } from "../../store/userDataSlice";
import { CpFormPromptDetail } from "../../components/antd/form/CpFormPromptDetail";
import { CpFormContext } from "../../components/form/CpFormContext";
import { CpFormValidator } from "../gvc/testNewForm/CpFormValidator";
import { CpPreviewModal } from "../workload/draft/previewModal";
import { CpDebugModal } from "../workload/draft/debugModal";
import { Org } from "../../schema/types/org/entity";
import { useGetOrgQuery } from "../../store/api/orgApi";
import { inputToOrgPatch, orgToInput, orgToPatch } from "../../schema/types/org/utils";
import { getValidatorDetail } from "./validatorDetail";
import { OrgInput } from "../../schema/types/org/input";
import { OrgDetailContent } from "./detailContent";
import { BillingAccountSummary } from "../../schema/types/account";
import { getDirtyChecker } from "./dirtyChecker";
import { CpFormDirtyChecker } from "../gvc/testNewForm/CpFormDirtyChecker";

interface Props {
  org: Org;
}
export const OrgDetailForm: React.FC<Props> = ({ org }) => {
  const dispatch = useAppDispatch();
  const isSaml = useAppSelector((state) => state.user.isSaml);
  const samlDomain = useAppSelector((state) => state.user.samlDomain);
  const lightSlug = useAppSelector(selectLightSlug);
  const darkSlug = useAppSelector(selectDarkSlug);

  const formIdRef = React.useRef(uuidv4());
  const formId = formIdRef.current;

  const { refetch } = useGetOrgQuery(org.name);

  const [isLoadingAccount, setIsLoadingAccount] = React.useState(true);
  const [account, setAccount] = React.useState<BillingAccountSummary | undefined>(undefined);

  React.useEffect(() => {
    updateAccount();
  }, [org.name]);

  async function updateAccount() {
    try {
      setIsLoadingAccount(true);
      const { data } = await request({ service: "billing-ng", url: `/org/${org.name}/account` });
      setAccount({
        id: data.id,
        fullName: data.fullName,
        accountName: data.accountName,
        company: data.extraInfo?.company,
      });
      setIsLoadingAccount(false);
    } catch (e) {
      setIsLoadingAccount(false);
      setAccount(undefined);
    }
  }

  // form init
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  React.useEffect(() => {
    const input = orgToInput(org, isSaml, samlDomain);
    formInitialize(formId, input);
    setIsInitialized(true);
    return () => {
      formUninitialize(formId);
    };
  }, []);

  // confirm
  React.useEffect(() => {
    dispatch(formActions.setInitialValues({ id: formId, values: orgToInput(org, isSaml, samlDomain) }));
  }, [org.version]);

  // validation/dirty/submit
  const validatorRef = React.useRef(getValidatorDetail(formId));
  const validator = validatorRef.current;
  const dirtyCheckerRef = React.useRef(getDirtyChecker(formId));
  const dirtyChecker = dirtyCheckerRef.current;
  const submit = useCpFormSubmit({ id: formId, validationFn: validator, onSubmit: handleSubmit });

  async function handleSubmit(input: OrgInput) {
    try {
      let body = orgToPatch(inputToOrgPatch(input), org, { id: input.id, version: input.version });
      const method = "patch";
      const url = linksOf(org).self!;

      await request({
        method: method,
        url: url,
        body: body,
      });

      formSet(formId, `meta.applied`, true);

      notification.success({
        message: "Success",
        description: "Org is updated",
      });

      const refetchResult = await refetch();
      if (refetchResult.data) {
        dispatch(updateOrg(refetchResult.data));
      }

      // Re-fetch custom logo only if logo-related tags changed
      const oldTags = org.tags || {};
      const newLogoTag = input.tags.find((t) => t.name === "cpln/custom-logo")?.value || "";
      const newLogoDarkTag = input.tags.find((t) => t.name === "cpln/custom-logo-dark")?.value || "";
      const oldLogoTag = oldTags["cpln/custom-logo"] || "";
      const oldLogoDarkTag = oldTags["cpln/custom-logo-dark"] || "";
      if (newLogoTag !== oldLogoTag || newLogoDarkTag !== oldLogoDarkTag) {
        clearLogoCacheForSlugs([lightSlug, darkSlug, newLogoTag, newLogoDarkTag].filter(Boolean));
        dispatch(fetchCustomLogo());
      }
    } catch (e) {
      if (e?.response?.status === 409) {
        Modal.warning({
          title: "Update Conflict",
          content: "This resource has been modified since you opened it. The form will be refreshed with the latest data and your changes will be lost.",
          onOk: () => {
            refetch();
          },
        });
        return;
      }

      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  if (!isInitialized) {
    return null;
  }

  return (
    <CpFormContext.Provider value={{ id: formId, submit: submit }}>
      <OrgDetailContent org={org} account={account} isLoadingAccount={isLoadingAccount} updateAccount={updateAccount} refetch={refetch} />
      <CpFormValidator validationFn={validator} />
      <CpFormDirtyChecker checker={dirtyChecker} />
      <CpFormPromptDetail link={linksOf(org).self!} inputToItemFn={inputToOrgPatch} blockChildren />
      <CpPreviewModal inputToItemFn={inputToOrgPatch} kind="org" namePath="name" title="Org" />
      <CpDebugModal inputToItemFn={inputToOrgPatch} kind="org" namePath="name" title="Org" />
    </CpFormContext.Provider>
  );
};
